import React, { Component } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Grid } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import MusicNoteIcon from "@material-ui/icons/MusicNote";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";

import YoutubeWidget from "../widget/YoutubeWidget";
import VimeoWidget from "../widget/VimeoWidget";
import SoundCloudWidget from "../widget/SoundCloudWidget";
import SpotifyWidget from "../widget/SpotifyWidget";

import "./MediaPreviewModal.css";

const youtubeReg = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i;
const soundcloudReg = /^https?:\/\/(www\.)?(soundcloud\.com|w\.soundcloud\.com)/i;
const spotifyReg = /^https?:\/\/(open\.spotify\.com)/i;
const vimeoReg = /^https?:\/\/(www\.)?(vimeo\.com|player\.vimeo\.com)/i;

class MediaPreviewModal extends Component {
    state = {
        lightboxOpen: false,
        lightboxIndex: 0
    };

    openLightbox = (index) => {
        this.setState({ lightboxOpen: true, lightboxIndex: index });
    };

    closeLightbox = () => {
        this.setState({ lightboxOpen: false });
    };

    navigateLightbox = (direction) => {
        const { photos } = this.props;
        if (!photos || photos.length === 0) return;
        
        let newIndex = this.state.lightboxIndex + direction;
        if (newIndex < 0) newIndex = photos.length - 1;
        if (newIndex >= photos.length) newIndex = 0;
        this.setState({ lightboxIndex: newIndex });
    };

    handleLightboxKeyDown = (e) => {
        if (e.key === 'Escape') this.closeLightbox();
        if (e.key === 'ArrowLeft') this.navigateLightbox(-1);
        if (e.key === 'ArrowRight') this.navigateLightbox(1);
    };

    renderVideo = (video, index) => {
        const url = video.url || video;
        if (url.match(youtubeReg)) {
            return <YoutubeWidget key={index} url={url} />;
        } else if (url.match(vimeoReg)) {
            return <VimeoWidget key={index} url={url} height="300px" />;
        }
        return (
            <div key={index} className="video-placeholder">
                <Typography variant="body2">Video: {url}</Typography>
            </div>
        );
    };

    renderAudio = (musicUrl) => {
        if (musicUrl.match(soundcloudReg)) {
            return <SoundCloudWidget url={musicUrl} />;
        } else if (musicUrl.match(spotifyReg)) {
            return <SpotifyWidget url={musicUrl} />;
        }
        return (
            <div className="audio-placeholder">
                <Typography variant="body2">Audio: {musicUrl}</Typography>
            </div>
        );
    };

    getModalIcon = () => {
        const { type } = this.props;
        switch (type) {
            case 'videos':
                return <PlayArrowIcon style={{ marginRight: 8 }} />;
            case 'audio':
                return <MusicNoteIcon style={{ marginRight: 8 }} />;
            case 'gallery':
                return <PhotoLibraryIcon style={{ marginRight: 8 }} />;
            default:
                return null;
        }
    };

    getModalTitle = () => {
        const { type, profileName } = this.props;
        const name = profileName || "Act";
        switch (type) {
            case 'videos':
                return `${name}'s Videos`;
            case 'audio':
                return `${name}'s Sound`;
            case 'gallery':
                return `${name}'s Gallery`;
            default:
                return "Media";
        }
    };

    renderContent = () => {
        const { type, videos, sounds, photos } = this.props;

        switch (type) {
            case 'videos':
                if (!videos || videos.length === 0) {
                    return (
                        <div className="media-empty-state">
                            <PlayArrowIcon style={{ fontSize: 48, color: '#ccc' }} />
                            <Typography variant="body1" style={{ color: '#666', marginTop: 16 }}>
                                No videos available
                            </Typography>
                        </div>
                    );
                }
                return (
                    <Grid container spacing={16}>
                        {videos.map((video, index) => (
                            <Grid item xs={12} sm={videos.length === 1 ? 12 : 6} key={video.id || index}>
                                {this.renderVideo(video, index)}
                            </Grid>
                        ))}
                    </Grid>
                );

            case 'audio':
                if (!sounds || sounds.length === 0) {
                    return (
                        <div className="media-empty-state">
                            <MusicNoteIcon style={{ fontSize: 48, color: '#ccc' }} />
                            <Typography variant="body1" style={{ color: '#666', marginTop: 16 }}>
                                No audio available
                            </Typography>
                        </div>
                    );
                }
                return (
                    <Grid container spacing={16}>
                        {sounds.map((sound, index) => {
                            const url = sound?.url || sound;
                            return (
                                <Grid item xs={12} key={sound?.id || index}>
                                    {this.renderAudio(url)}
                                </Grid>
                            );
                        })}
                    </Grid>
                );

            case 'gallery':
                if (!photos || photos.length === 0) {
                    return (
                        <div className="media-empty-state">
                            <PhotoLibraryIcon style={{ fontSize: 48, color: '#ccc' }} />
                            <Typography variant="body1" style={{ color: '#666', marginTop: 16 }}>
                                No photos available
                            </Typography>
                        </div>
                    );
                }
                return (
                    <Grid container spacing={16} className="gallery-grid">
                        {photos.map((photo, index) => (
                            <Grid item xs={6} sm={4} md={3} key={photo.id || index}>
                                <div 
                                    className="gallery-item"
                                    onClick={() => this.openLightbox(index)}
                                >
                                    <img 
                                        src={photo.url || photo.url400Complete || photo.url960Complete} 
                                        alt={photo.name || `Photo ${index + 1}`}
                                    />
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                );

            default:
                return null;
        }
    };

    renderLightbox = () => {
        const { photos } = this.props;
        const { lightboxOpen, lightboxIndex } = this.state;
        
        if (!lightboxOpen || !photos || photos.length === 0) return null;
        
        const currentPhoto = photos[lightboxIndex];
        const photoUrl = currentPhoto.url || currentPhoto.url960Complete || currentPhoto.url1920 || currentPhoto.url400Complete;
        
        return (
            <div 
                className="media-lightbox-overlay"
                onClick={this.closeLightbox}
                onKeyDown={this.handleLightboxKeyDown}
                tabIndex={0}
            >
                <button className="lightbox-close" onClick={this.closeLightbox}>
                    <CloseIcon />
                </button>
                <button 
                    className="lightbox-nav lightbox-prev"
                    onClick={(e) => { e.stopPropagation(); this.navigateLightbox(-1); }}
                >
                    ‹
                </button>
                <img 
                    src={photoUrl}
                    alt={currentPhoto.name || `Photo ${lightboxIndex + 1}`}
                    className="lightbox-image"
                    onClick={(e) => e.stopPropagation()}
                />
                <button 
                    className="lightbox-nav lightbox-next"
                    onClick={(e) => { e.stopPropagation(); this.navigateLightbox(1); }}
                >
                    ›
                </button>
                <div className="lightbox-counter">
                    {lightboxIndex + 1} / {photos.length}
                </div>
            </div>
        );
    };

    render() {
        const { open, onClose } = this.props;

        return (
            <>
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="md"
                    fullWidth
                    className="media-preview-modal"
                >
                    <DialogTitle disableTypography className="media-modal-title">
                        <div className="title-content">
                            {this.getModalIcon()}
                            <span>{this.getModalTitle()}</span>
                        </div>
                        <IconButton className="close-button" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className="media-modal-content">
                        {this.renderContent()}
                    </DialogContent>
                </Dialog>
                {this.renderLightbox()}
            </>
        );
    }
}

export default MediaPreviewModal;
